import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getOptimizedSelector } from "store/utils/get-optimized-selector.utils";

export const STAGED_LANGUAGE_STORAGE_KEY = "ffair_staged_language";

type PersistState = "idle" | "pending" | "failed";

type State = {
  code: string | null;
  persistState: PersistState;
};

type Action = {
  stage: (code: string) => void;
  clear: () => void;
  markPending: () => void;
  markFailed: () => void;
};

const readStoredCode = (): string | null => {
  try {
    return localStorage.getItem(STAGED_LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredCode = (code: string | null): void => {
  try {
    if (code) {
      localStorage.setItem(STAGED_LANGUAGE_STORAGE_KEY, code);
    } else {
      localStorage.removeItem(STAGED_LANGUAGE_STORAGE_KEY);
    }
  } catch {
    // Without storage the staged code still lives for this tab.
  }
};

// Not registered for `resetAllStores`: `logoutCleanup` decides whether a
// language picked before sign-in survives the logout.
export const stagedLanguageStore = create(
  devtools<State & Action>((set) => ({
    code: readStoredCode(),
    persistState: "idle",
    stage: (code) => {
      writeStoredCode(code);
      set({ code, persistState: "idle" });
    },
    clear: () => {
      writeStoredCode(null);
      set({ code: null, persistState: "idle" });
    },
    markPending: () => set({ persistState: "pending" }),
    // Keeps the code for this tab only, so a failed save is not retried.
    markFailed: () => {
      writeStoredCode(null);
      set({ persistState: "failed" });
    },
  }))
);

export const useStagedLanguageStore = getOptimizedSelector(stagedLanguageStore);
